/* eslint-disable max-lines-per-function */
import UFOLoadHold from '@atlaskit/react-ufo/load-hold';
import { breakpointValues, Button, useResponsiveBreakpoint } from '@atlassian/mpac-primitives';
import React from 'react';
import { AppTileGridSkeletonCompact } from '../../primitives';
import { AppTileCompact } from '../AppTile';
import { AppGridSection, getGridVars } from './AppGridSection';
import { AtlassianAppGridSection } from './AtlassianAppGridSection';
import { GRID_CONFIG } from './constants';
import { FetchMoreError, InitialFetchError } from './PaginatedAppGrid';
import { ButtonContainer, GridFooterContainer } from './PaginatedAppGrid.styles';
import { jsx as _jsx, Fragment as _Fragment, jsxs as _jsxs } from "react/jsx-runtime";
export const PaginatedAppGridWithAtlassianApps = _ref => {
  let {
    isLoading,
    isLoadingMore,
    onLoadMoreClick,
    showLoadMoreButton,
    fetchMoreError,
    initialFetchError,
    desktopColumns = 3,
    tabletColumns = 2,
    mobileColumns = 1,
    isSearchQueryActive = false,
    areFiltersActive = false,
    appList = [],
    environment,
    source,
    onTileClick: _onTileClick,
    tileToFocusIndex,
    handleTileToFocus,
    selectedHostingFilter,
    atlassianAppList = [],
    onAtlassianAppSectionViewed
  } = _ref;
  const {
    value: breakpoint
  } = useResponsiveBreakpoint();
  const gridVars = getGridVars(mobileColumns, tabletColumns, desktopColumns);
  const loadingMoreTileCount = breakpoint < breakpointValues.md ? tabletColumns : desktopColumns;
  if (initialFetchError) {
    return /*#__PURE__*/_jsx(InitialFetchError, {});
  }

  /**
   * Renders a single app tile with the provided configuration
   */
  const renderAppTile = (app, index) => /*#__PURE__*/_jsx(AppTileCompact, {
    tileData: app,
    environment: environment,
    source: source,
    onTileClick: () => _onTileClick === null || _onTileClick === void 0 ? void 0 : _onTileClick(app, index),
    tileRef: index === tileToFocusIndex ? handleTileToFocus : undefined,
    preferredHosting: selectedHostingFilter
  }, index);

  /**
   * Determines whether to show the sectioned layout (Featured, Atlassian, All apps)
   * or a simple flat list
   */
  const shouldShowSectionedLayout = () => {
    return !isSearchQueryActive && !areFiltersActive && atlassianAppList.length > 0;
  };

  /**
   * Renders apps in a sectioned layout: Featured apps, Atlassian apps, All apps
   * - Featured apps: Shows first 3 apps (or all if less than 3)
   * - All apps: Only shown if there are more than 3 apps total
   */
  const renderSectionedApps = () => {
    const featuredApps = appList.slice(0, GRID_CONFIG.FEATURED_APPS_COUNT);
    const remainingApps = appList.slice(GRID_CONFIG.FEATURED_APPS_COUNT);
    const atlassianApps = atlassianAppList.slice(0, GRID_CONFIG.ATLASSIAN_APP_TILES_COUNT);
    return /*#__PURE__*/_jsxs(_Fragment, {
      children: [featuredApps.length > 0 && /*#__PURE__*/_jsx(AppGridSection, {
        title: "Featured apps",
        gridVars: gridVars,
        children: featuredApps.map((app, index) => renderAppTile(app, index))
      }), atlassianApps.length > 0 && /*#__PURE__*/_jsx(AtlassianAppGridSection, {
        gridVars: gridVars,
        atlassianApps: atlassianApps,
        onAtlassianAppSectionViewed: onAtlassianAppSectionViewed
      }), remainingApps.length > 0 && /*#__PURE__*/_jsx(AppGridSection, {
        title: "All apps",
        gridVars: gridVars,
        children: remainingApps.map((app, index) => renderAppTile(app, index + GRID_CONFIG.FEATURED_APPS_COUNT))
      })]
    });
  };

  /**
   * Renders all apps in a simple flat list without sections
   */
  const renderFlatAppList = () => {
    if (appList.length === 0) {
      return null;
    }
    return /*#__PURE__*/_jsx(AppGridSection, {
      gridVars: gridVars,
      children: appList.map(renderAppTile)
    });
  };

  /**
   * Main rendering logic that decides between sectioned or flat layout
   */
  const renderApps = () => {
    if (appList.length === 0) {
      return null;
    }
    return shouldShowSectionedLayout() ? renderSectionedApps() : renderFlatAppList();
  };
  return /*#__PURE__*/_jsxs("div", {
    children: [isLoading ? /*#__PURE__*/_jsx(AppGridSection, {
      gridVars: gridVars,
      children: /*#__PURE__*/_jsx(UFOLoadHold, {
        name: "app-tile",
        children: /*#__PURE__*/_jsx(AppTileGridSkeletonCompact, {
          tileCount: desktopColumns * GRID_CONFIG.INITIAL_SKELETON_TILE_MULTIPLIER
        })
      })
    }) : renderApps(), isLoadingMore && /*#__PURE__*/_jsx(AppGridSection, {
      gridVars: gridVars,
      children: /*#__PURE__*/_jsx(AppTileGridSkeletonCompact, {
        tileCount: loadingMoreTileCount * GRID_CONFIG.LOAD_MORE_SKELETON_TILE_MULTIPLIER
      })
    }), /*#__PURE__*/_jsxs(GridFooterContainer, {
      children: [fetchMoreError && /*#__PURE__*/_jsx(FetchMoreError, {}), showLoadMoreButton && /*#__PURE__*/_jsx(ButtonContainer, {
        children: /*#__PURE__*/_jsx(Button, {
          isLoading: isLoadingMore,
          onClick: onLoadMoreClick,
          "aria-label": "Load more apps",
          children: "Load more"
        })
      })]
    })]
  });
};